import { type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';

const BATCH_MS = 250;
interface RunningRefresh { dirty: boolean; promise: Promise<void> }
interface Queue {
  timer?: ReturnType<typeof setTimeout>;
  targets: Map<string, QueryKey>;
  running: Map<Query, RunningRefresh>;
  disposed: boolean;
}
const queues = new WeakMap<QueryClient, Queue>();

function queueFor(client: QueryClient): Queue {
  let queue = queues.get(client);
  if (!queue) {
    queue = { targets: new Map(), running: new Map(), disposed: false };
    queues.set(client, queue);
  }
  return queue;
}

function runRefresh(client: QueryClient, queue: Queue, query: Query, event: boolean): Promise<void> {
  const running = queue.running.get(query);
  if (running) {
    // Clicks join the read already underway. A new database event requires a
    // trailing read so changes committed during that read cannot be missed.
    if (event) running.dirty = true;
    return running.promise;
  }
  const state: RunningRefresh = { dirty: false, promise: Promise.resolve() };
  queue.running.set(query, state);
  state.promise = (async () => {
    try {
      if (query.state.fetchStatus === 'fetching') await query.promise?.catch(() => undefined);
      let failure:{error:unknown}|null=null;
      do {
        failure=null;
        state.dirty = false;
        if (queue.disposed || client.getQueryCache().get(query.queryHash) !== query) return;
        try {
          await client.invalidateQueries(
            { queryKey: query.queryKey, exact: true },
            { cancelRefetch: false, throwOnError: true },
          );
        } catch (error) { failure={error}; }
      } while (state.dirty && !queue.disposed);
      if (failure) throw failure.error;
    } finally {
      queue.running.delete(query);
    }
  })();
  return state.promise;
}

function matchingQueries(client: QueryClient, keys: Iterable<QueryKey>): Set<Query> {
  const queries = new Set<Query>();
  for (const key of keys) {
    for (const query of client.getQueryCache().findAll({ queryKey: key })) queries.add(query);
  }
  return queries;
}

/** Awaitable manual/after-save refresh, using the SAME queue as realtime. */
export async function refreshQueries(client: QueryClient, keys: readonly QueryKey[], options:{afterMutation?:boolean}={}): Promise<void> {
  const queue = queueFor(client);
  // Flush queued events together with the click, not a second request 250ms later.
  const events = matchingQueries(client, queue.targets.values());
  const queries = matchingQueries(client, [...queue.targets.values(), ...keys]);
  clearTimeout(queue.timer);
  queue.timer = undefined;
  queue.targets.clear();
  await Promise.all([...queries].map(query => runRefresh(client, queue, query, events.has(query) || options.afterMutation===true)));
}

/** Share one refresh queue across the provider and page-specific listeners. */
export function scheduleRealtimeInvalidation(client: QueryClient, queryKey: QueryKey): void {
  const queue = queueFor(client);
  queue.targets.set(JSON.stringify(queryKey), queryKey);
  if (queue.timer !== undefined) return;
  // Fixed windows also flush during sustained traffic (no debounce starvation).
  queue.timer = setTimeout(() => {
    queue.timer = undefined;
    const queries = matchingQueries(client, queue.targets.values());
    queue.targets.clear();
    for (const query of queries) {
      // Automatic failures live on the query; manual callers receive rejection.
      void runRefresh(client, queue, query, true).catch(() => undefined);
    }
  }, BATCH_MS);
}

export function clearRealtimeInvalidations(client: QueryClient): void {
  const queue = queues.get(client);
  if (!queue) return;
  queue.disposed = true;
  clearTimeout(queue.timer);
  queue.targets.clear();
  queues.delete(client);
}
